import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { nameBasedProtectedRoutes } from "router/protected";

import { Button, Card, Col, Row } from "react-bootstrap";
import MaterialReactTable, {
  type MRT_ColumnDef,
  type MRT_SortingState,
  type MRT_PaginationState,
} from "material-react-table";
import moment from "moment";
import InputSelect from "../../components/common/InputSelect";
import {
  datetimeFormats,
  machineStatusType,
  machineStatusList,
} from "../../constants/constants";
import InputText from "components/common/InputText";
import {
  downloadCertificateApiCall,
  downloadDocumentsApiCall,
  inspectionsApiCall,
} from "services/inspections";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import ImageTag from "components/common/ImageTag";
import DownloadIocon from "assets/images/download-icon.svg";
import { toast } from "react-toastify";
import { getInspectorsApiCall } from "services/authService";
import InputDateOnlyPicker from "components/common/InputDateOnlyPicker";
import TablePagination from "@mui/material/TablePagination";
import Pagination from "@mui/material/Pagination";
import ShareModel from "./ShareModel";

/**
 * Create a custom component which will render today's inspection section in dashboard.
 * @param props
 * @returns Today's Inspection component
 */
export const DataTable = (props: { userId: number }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state;
  const [sortColumn, setSortColumn] = useState("Inspection_machine.updated_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [sorting, setSorting] = useState<MRT_SortingState>([
    { id: sortColumn, desc: false },
  ]); // The default sorting data to show status column sort icon
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [inspectionDate, setInspectionDate] = useState("");
  const [machineStatus, setMachineStatus] = useState(
    state?.activeCertificate ? machineStatusType.active : "all"
  );
  const [inspector, setInspector] = useState("all");
  const [srNumber, setSrNumber] = useState("");
  const [status, setStatus] = useState("all");
  const [inspectorList, setInspectorList] = useState([]);
  const [inspectionStatus, setInspectionStatus] = useState([]);
  const [loadData, setLoadData] = useState(false);
  const [searchData, setSearchData] = useState({
    status: status,
    inspectionDate: inspectionDate,
    search: search,
    srNumber: srNumber,
    inspector: inspector,
    machineStatus: machineStatus,
  });
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [fromRows, setFromRows] = useState(0);
  const [toRows, setToRows] = useState(0);
  const [shareModelVisible,setShareModelVisible] = useState(false)
  const [shareData,setShareData] = useState({})

  useEffect(() => {
    getInspectors();
  }, [sorting, searchData, pagination.pageIndex, pagination.pageSize]);

  // Fetch all the inspected machine list with inspector list
  let inspectionType;
  const getInspectors = () => {
    if (!loadData) {
      getInspectorsApiCall().then((response) => {
        const inspectorData = response?.data;
        const inspectionStatusOption = response?.inspectionType;
        setInspectorList(inspectorData);
        setInspectionStatus(inspectionStatusOption);
        inspectionType = inspectionStatusOption;
        setLoadData(true);
        fetchinspectedMachines();
      });
    } else {
      fetchinspectedMachines();
    }
  };

  // Fetch all inspected machine with pagination, search and sorting
  const fetchinspectedMachines = async () => {
    const param = {
      sortColumn: 0 in sorting ? sorting[0].id : sortColumn,
      sortDirection:
        0 in sorting
          ? sorting[0].desc === false
            ? "asc"
            : "desc"
          : sortDirection,
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      ...searchData,
    };

    inspectionsApiCall(param, props.userId).then(async (response) => {
      const machineData = response.resultRows;
      const newData = await Promise.all(
        machineData.map((obj: any) => {
          const machine = obj.Inspection_machine;
          if (machine.inspection_type == "" || machine.inspection_type == null)
            return obj;
          inspectionType = inspectionType ? inspectionType : inspectionStatus;
          const findValue = inspectionType.find(
            (item: any) => item.key === machine.inspection_type
          );

          if (findValue) {
            const newMachine = { ...machine, inspection_type: findValue.value };
            return { ...obj, Inspection_machine: newMachine };
          }
          return obj;
        })
      );
      setData(newData);
      setTotalPages(response.totalPages);
      setRowCount(response.totalResults);

      const fromRowsVal =
        newData.length > 0 ? (response.page - 1) * response.limit + 1 : 0;
      let toRowsVal = fromRowsVal + newData.length - 1;
      toRowsVal = toRowsVal > 0 ? toRowsVal : 0;
      setFromRows(fromRowsVal);
      setToRows(toRowsVal);
    });
  };

  // Search list of inspected machine as per the data added in input fileds
  const submitSearch = () => {
    const submitedData = {
      status: status,
      inspectionDate: inspectionDate,
      search: search,
      srNumber: srNumber,
      inspector: inspector,
      machineStatus: machineStatus,
    };
    const pagi = { ...pagination, pageIndex: 0 };
    setPagination(pagi);
    setSearchData(submitedData);
  };

  // set inspection date on selecting from date picker
  const inspectionDateFilter = (data: any) => {
    if (data) {
      // since date range picker gives array containing start and end date, we are converting both the dates to YYYY-MM-DD format
      const startDate = moment(data).format(datetimeFormats.databaseFormat);

      setInspectionDate(startDate);
    } else {
      setInspectionDate("");
    }
  };

  // set search data in useState variable
  const searchCertificate = (event: any) => {
    setSearch(event.target.value);
  };

  // set search data in useState variable
  const searchSrNumber = (event: any) => {
    setSrNumber(event.target.value);
  };

  // set search data in useState variable
  const statusFilter = (event: any) => {
    setStatus(event.target.value);
  };

  // set search data in useState variable
  const machineStatusFilter = (event: any) => {
    setMachineStatus(event.target.value);
  };

  // set search data in useState variable
  const inspectorFilter = (event: any) => {
    setInspector(event.target.value);
  };

  // Download documents of the inspected machine
  const downloadDocuments = (id) => {
    downloadDocumentsApiCall(id)
      .then((response) => {
        window.open(response.data, "_blank");
      })
      .catch((error) => {
        toast.error(error.response.data.message);
      });
  };

  // Download cetificate of the inspected machine
  const downloadCertificate = (id) => {
    downloadCertificateApiCall(id)
      .then((response) => {
        window.open(response.data, "_blank");
      })
      .catch((error) => {
        toast.error(error.response.data.message);
      });
  };

  /* Column Definition requires for MaterialReactTable */
  type InspectedMachine = {
    current_user: boolean;
    machine_id: number;
    customer_id: number;
    document_transferred: boolean;
    Inspection_machine: {
      id: number;
      inspection_id: number;
      user_id: number;
      device_id: string;
      make: string;
      model_number: string;
      serial_number: string;
      inspection_type: string;
      status: string;
      Inspection: {
        id: number;
        sr_number: string;
        inspection_date: string;
      };
      certificate: {
        issue_date: string;
        expire_date: string;
        machine_status: string;
      };
    };
  };

  const shareDocumentModel = (row : any) =>{
    
    setShareData(row)
    setShareModelVisible(!shareModelVisible)
}

const handleCloseModel = () => {
  setShareData({})
  setShareModelVisible(false)
}
  // Mentioning table columns belows.
  const tableColumns = useMemo<MRT_ColumnDef<InspectedMachine>[]>(
    () => [
      {
        accessorKey: "Inspection_machine.inspection_id",
        header: "SR NO.",
        Cell: ({ cell, row }) =>
          row.original?.customer_id ==
          row.original?.Inspection_machine?.user_id ? (
            <Link
              to={`${nameBasedProtectedRoutes.inspectionDetail.path}/${row.original?.Inspection_machine?.Inspection?.id}`}
              className="text-decoration-none text-link"
            >
              <strong>
                {row.original?.Inspection_machine?.Inspection?.sr_number}
              </strong>
            </Link>
          ) : (
            <strong>
              {row.original?.Inspection_machine?.Inspection?.sr_number}
            </strong>
          ),
      },
      {
        accessorKey: "Inspection_machine.device_id",
        header: "DEVICE ID",
        Cell: ({ cell, row }) => {
          return row.original?.Inspection_machine?.device_id;
        },
      },
      {
        accessorKey: "Inspection_machine.make",
        header: "MAKE",
        Cell: ({ cell, row }) => row.original?.Inspection_machine?.make,
      },
      {
        accessorKey: "Inspection_machine.model_number",
        header: "MODEL NO.",
        Cell: ({ cell, row }) => row.original?.Inspection_machine?.model_number,
      },
      {
        accessorKey: "Inspection_machine.serial_number",
        header: "SERIAL NO.",
      },
      {
        accessorKey: "Inspection_machine.Inspection.inspection_date",
        header: "INSPECTION DATE",
        Cell: ({ cell, row }) => {
          return row.original?.Inspection_machine?.Inspection?.inspection_date
            ? moment(
                `${row.original?.Inspection_machine?.Inspection?.inspection_date}`
              ).format(datetimeFormats.separatedDate)
            : "-";
        },
      },
      {
        accessorKey: "Inspection_machine.inspection_type",
        header: "INSPECTION TYPE",
        Cell: ({ cell, row }) => {
          return row.original?.Inspection_machine?.inspection_type;
        },
      },
      {
        accessorKey: "Inspection_machine.certificate.issue_date",
        header: "CERTIFICATE DATE",
        Cell: ({ cell, row }) => {
          return row.original?.Inspection_machine?.certificate?.issue_date
            ? moment(
                `${row.original?.Inspection_machine?.certificate?.issue_date}`
              ).format(datetimeFormats.separatedDate)
            : "-";
        },
      },
      {
        accessorKey: "Inspection_machine.certificate.expire_date",
        header: "STATUS",
        Cell: ({ cell, row }) => {
          switch (
            row.original?.Inspection_machine?.certificate?.machine_status
          ) {
            case machineStatusType.expired:
              return (
                <span className="text-danger bg-light-red fs-15  p-1 pe-2 ps-2 text-light-red cap-text border-radius-5">
                  {machineStatusType.expired}
                </span>
              );
            case machineStatusType.active:
              return (
                <span className="text-success bg-ligt-green fs-15 p-1 pe-2 ps-2 text-green cap-text border-radius-5">
                  {machineStatusType.active}
                </span>
              );
            default:
              return row.original?.Inspection_machine?.status?.toUpperCase();
          }
        },
      },
      {
        accessorKey: "machine_id",
        header: "ACTION",
        enableSorting: false,
        Cell: ({ cell, row }) => {
          return (
            <>
              <OverlayTrigger
                overlay={<Tooltip>Download Document Set</Tooltip>}
              >
                <span
                  role="button"
                  onClick={() =>
                    row.original?.customer_id ==
                      row.original?.Inspection_machine?.user_id ||
                    row.original?.document_transferred
                      ? downloadDocuments(row.original?.machine_id)
                      : downloadCertificate(row.original?.machine_id)
                  }
                  className="d-inline-block"
                >
                  <ImageTag src={DownloadIocon} alt="Download Document Set" />
                </span>
              </OverlayTrigger>{" "}
              <i
            onClick={()=>{
              shareDocumentModel(row.original)
            }}
            className="pointer-cursor fas fa fa-share-alt mx-2 text-link"
          />
            </>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="col-xl-12 mb-3">
      <Card.Body className="br-10 p-4 new-table-design">
        <Row className="">
          <Col className="billing-serch-input col-md-12 col-lg-3  col-12">
            <InputText
              controlId="search"
              label=""
              placeholder="Search"
              touched={false}
              handleBlur={false}
              handleChange={searchCertificate}
              errorsField={false}
              value={search}
            />
          </Col>
          <Col className="inspection-machine-input-right col-md-12 col-lg-9 col-12">
            <Row className="float-end ">
              <Col className="inspection-machine-sr">
                <InputText
                  controlId="sr_number"
                  label="SR No."
                  placeholder="SR No."
                  touched={false}
                  handleBlur={false}
                  handleChange={searchSrNumber}
                  errorsField={false}
                  value={srNumber}
                />
              </Col>
              <Col className="inspector-date-icon  dashboard-input-cover">
                <InputDateOnlyPicker
                  controlId="inspection_date"
                  label="Inspection Date"
                  placeholder="Inspection Date"
                  touched={false}
                  handleBlur={false}
                  handleChange={inspectionDateFilter}
                  handleClose={inspectionDateFilter}
                  errorsField={false}
                  value={false}
                />
              </Col>
              {loadData && (
                <>
                  {" "}
                  <Col className="dashboard-input-cover">
                    <InputSelect
                      controlId="inspector"
                      label="All Examinar"
                      placeholder="All Examinar"
                      touched={false}
                      handleBlur={false}
                      handleChange={inspectorFilter}
                      errorsField={false}
                      value={inspector}
                      options={[
                        { key: "all", value: "All Examinar" },
                        ...inspectorList,
                      ]}
                    />
                  </Col>
                  <Col className="dashboard-input-cover">
                    <InputSelect
                      controlId="status"
                      label="Inspection Type"
                      placeholder="Inspection Type"
                      touched={false}
                      handleBlur={false}
                      handleChange={statusFilter}
                      errorsField={false}
                      value={status}
                      options={[
                        { key: "all", value: "All Types" },
                        ...inspectionStatus,
                      ]}
                    />
                  </Col>{" "}
                </>
              )}
              <Col className="dashboard-input-cover">
                <InputSelect
                  controlId="machineStatus"
                  label="Status"
                  placeholder="Status"
                  touched={false}
                  handleBlur={false}
                  handleChange={machineStatusFilter}
                  errorsField={false}
                  value={machineStatus}
                  options={[
                    { key: "all", value: "All Status" },
                    ...machineStatusList,
                  ]}
                />
              </Col>
              <Col className="col-auto">
                <Button onClick={submitSearch} className="fw-semibold">
                  Submit
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
        <div className="table-responsive ">
          <MaterialReactTable
            muiTableProps={{
              classes: {
                root: "table custom-table border-none-table table-striped machine-custome-table",
              },
            }}
            enableGlobalFilter={false}
            enableFullScreenToggle={false}
            enableDensityToggle={false}
            enableHiding={false}
            enableColumnFilters={false}
            enablePagination={false}
            columns={tableColumns}
            data={data}
            onSortingChange={setSorting}
            state={{ sorting }}
          />
          {totalPages > 0 ? (
            <div className="paggination">
              <p className="showing-text">
                Showing {fromRows}-{toRows} of {rowCount}
              </p>
              <Pagination
                count={totalPages}
                page={pagination.pageIndex + 1}
                shape="rounded"
                variant="outlined"
                onChange={(event, newPage) => {
                  const pagi = { ...pagination, pageIndex: newPage - 1 };
                  setPagination(pagi);
                }}
              />
            </div>
          ) : (
            ""
          )}
        </div>
        {shareModelVisible &&  <ShareModel
            handleCloseModel={handleCloseModel}
            shareModelVisible={shareModelVisible}  data={shareData}/>}
      </Card.Body>
    </div>
  );
};
